import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type DocumentView = Awaited<ReturnType<typeof trpc.documents.forWorkObject.query>>;

// A work object's rich description document (auto-created on first read).
export function useWorkObjectDocument(workObjectId: string | null) {
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const query = useQuery(trpcClient.documents.forWorkObject.queryOptions(workObjectId ? { workObjectId } : skipToken));
  const updateMutation = useMutation(trpcClient.documents.updateContent.mutationOptions());

  return {
    data: query.data ?? null,
    persist: async (input: { content: Record<string, unknown>; documentId: string; version: number }) => {
      const updated = await updateMutation.mutateAsync(input);
      queryClient.setQueryData(trpcClient.documents.forWorkObject.queryKey({ workObjectId: workObjectId ?? "" }), updated);
      return updated;
    },
    reload: async () => {
      if (!workObjectId) return;
      await queryClient.invalidateQueries({ queryKey: trpcClient.documents.forWorkObject.queryKey({ workObjectId }) });
    },
  };
}

// The page document behind a "document" board view (auto-created on first read).
export function useViewDocument(boardViewId: string | null) {
  const trpcClient = useTRPC();
  const queryClient = useQueryClient();
  const query = useQuery(trpcClient.documents.forView.queryOptions(boardViewId ? { boardViewId } : skipToken));
  const updateMutation = useMutation(trpcClient.documents.updateContent.mutationOptions());

  return {
    data: query.data ?? null,
    persist: async (input: { content: Record<string, unknown>; documentId: string; version: number }) => {
      const updated = await updateMutation.mutateAsync(input);
      queryClient.setQueryData(trpcClient.documents.forView.queryKey({ boardViewId: boardViewId ?? "" }), updated);
      return updated;
    },
    reload: async () => {
      if (!boardViewId) return;
      await queryClient.invalidateQueries({ queryKey: trpcClient.documents.forView.queryKey({ boardViewId }) });
    },
  };
}
